import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchMediaRankings, fetchPlatforms, fetchRegions, mergeAndSortRegions, DEFAULT_UI_REGIONS } from '../services/api';
import { MediaGridSkeleton } from './common';
import MediaCard from './MediaCard';
import { useMediaFilters } from '../contexts/FilterContext';
import './PlatformMediaView.css';

// Regions for media: same as tags (global, sg, us, uk) — use DEFAULT_UI_REGIONS + merge

/**
 * Platform Media View Component
 * Shows latest media organized by platform (primary) with media type filter (secondary)
 * 
 * @param {Object} props
 * @param {string} props.initialPlatform - Initial platform to select (default: 'youtube')
 * @param {Function} props.onMediaClick - Callback when media is clicked
 */
function PlatformMediaView({
  initialPlatform = 'youtube',
  initialMediaType = 'video',
  initialRegion = 'GLOBAL',
  onMediaClick
}) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [regions, setRegions] = useState([]);
  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);

  // Filters - persisted in sessionStorage via FilterContext
  // This ensures filters are remembered when navigating back to this view
  const { filters: savedFilters, setFilter } = useMediaFilters();
  
  // Use saved filters, with initialProps as fallback only on first load
  const selectedPlatform = savedFilters?.platform || initialPlatform || 'youtube';
  const selectedMediaType = savedFilters?.media_type || initialMediaType || 'video';
  const selectedRegion = savedFilters?.region || initialRegion || 'GLOBAL';
  const limit = savedFilters?.limit || 30;

  // Setter functions that update the context - memoized to prevent infinite loops
  const setSelectedRegion = useCallback((value) => setFilter('region', value), [setFilter]);
  const setLimit = useCallback((value) => setFilter('limit', value), [setFilter]);

  // Platform info
  const [platformInfo, setPlatformInfo] = useState(null);


  // Load initial data (platforms and regions list only) - runs once on mount
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;

    const loadInitialData = async () => {
      try {
        const [platformsData, regionsData] = await Promise.all([
          fetchPlatforms(),
          fetchRegions()
        ]);
        // Support YouTube and TikTok
        const supportedPlatforms = Array.isArray(platformsData) ? platformsData : [];
        setPlatforms(supportedPlatforms);
        setRegions(mergeAndSortRegions(regionsData));
        
        // Only set platform if no saved filter exists and current platform is invalid
        if (!savedFilters?.platform) {
          const validPlatform = supportedPlatforms.find(p => p.id === initialPlatform) 
            ? initialPlatform 
            : (supportedPlatforms[0]?.id || 'youtube');
          setFilter('platform', validPlatform);
        }
      } catch (err) {
        console.error('Failed to load initial data:', err);
        setRegions(DEFAULT_UI_REGIONS);
        setPlatforms([
          { id: 'youtube', name: 'YouTube', icon: '📺' }
        ]);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency - runs once on mount (initialPlatform, savedFilters, setFilter are stable)

  // Memoize filter params to prevent infinite loops
  const mediaParams = useMemo(() => ({
    region: selectedRegion,
    platform: selectedPlatform,
    media_type: selectedMediaType,
    limit: limit,
    offset: 0
  }), [selectedRegion, selectedPlatform, selectedMediaType, limit]);

  // Load media when filters change
  useEffect(() => {
    // Prevent concurrent requests
    if (isLoadingRef.current) {
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();
    isLoadingRef.current = true;
    
    const loadMedia = async () => {
      setLoading(true);
      setError(null);
      // Clear media immediately when region changes to prevent showing stale data
      setMedia([]);
      
      try {
        const response = await fetchMediaRankings(mediaParams, { signal: abortController.signal });
        
        // Only update state if this request hasn't been cancelled
        if (!cancelled) {
          const items = response?.data?.items || [];
          setMedia(items);
          setPlatformInfo({
            platform: mediaParams.platform,
            platform_name: platforms.find(p => p.id === mediaParams.platform)?.name || mediaParams.platform,
            total_items: items.length,
            last_updated: new Date().toISOString()
          });
        }
      } catch (err) {
        // Ignore abort errors (they're expected when cancelling)
        if (err.name === 'AbortError') {
          return;
        }
        // Only show error if request wasn't cancelled
        if (!cancelled) {
          setError('Failed to load media. Please try again.');
          console.error('Failed to load media:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          isLoadingRef.current = false;
        }
      }
    };
    
    loadMedia();
    
    // Cleanup: cancel request if component unmounts or filters change
    return () => {
      cancelled = true;
      isLoadingRef.current = false;
      abortController.abort();
    };
  }, [mediaParams, platforms]);

  const handleMediaClick = (trendId) => {
    if (onMediaClick) {
      onMediaClick(trendId);
    }
  };

  return (
    <div className="platform-media-view">
      {/* Header */}
      <header className="media-view-header">
        <div className="header-title-section">
          <h1>📺 Top YouTube Videos</h1>
          <p className="subtitle">Ranked by trending score • {selectedRegion === 'GLOBAL' ? 'Worldwide' : regions.find(r => r.region_code === selectedRegion)?.region_name || selectedRegion}</p>
        </div>
      </header>

      {/* Region Pills */}
      <div className="region-pills">
        {regions.slice(0, 4).map(region => (
          <button
            key={region.region_code}
            className={`region-pill ${selectedRegion === region.region_code ? 'active' : ''}`}
            onClick={() => setSelectedRegion(region.region_code)}
          >
            {region.region_code === 'GLOBAL' && '🌐'}
            {region.region_code === 'SG' && '🇸🇬'}
            {region.region_code === 'US' && '🇺🇸'}
            {region.region_code === 'GB' && '🇬🇧'}
            {' '}{region.region_name}
          </button>
        ))}
      </div>

      {/* Filters Row */}
      <div className="filters-row">
        <div className="results-info">
          {platformInfo && (
            <span className="results-count">
              {platformInfo.total_items} videos
            </span>
          )}
        </div>
        <div className="limit-selector">
          <label>Show:</label>
          <select value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))}>
            <option value={30}>Top 30</option>
            <option value={50}>Top 50</option>
            <option value={100}>Top 100</option>
          </select>
        </div>
      </div>

      {/* Loading State - Skeleton */}
      {loading && <MediaGridSkeleton count={6} />}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => setSelectedRegion(selectedRegion)}>
            Try Again
          </button>
        </div>
      )}

      {/* Media Grid */}
      {!loading && !error && media.length > 0 && (
        <div className="media-grid">
          {media.map((item) => (
            <MediaCard
              key={item.trend_id}
              media={item}
              onClick={() => handleMediaClick(item.trend_id)}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && media.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No videos found</h3>
          <p>Try selecting a different region or check back later.</p>
        </div>
      )}
    </div>
  );
}

export default PlatformMediaView;
